import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, Award, DollarSign } from "lucide-react";
import { API } from "@/lib/config";
import { InquiryManager } from "@/admin/InquiryManager";
import EmailSettings from "@/admin/EmailSettings";
import WebsiteContent from "@/admin/WebsiteContent";

const HEART_LOGO = "https://customer-assets.emergentagent.com/job_0e489a82-2f44-4016-bd13-c06ced2d5c32/artifacts/wjrgajx6_AGG%20HEART%20LOGO.png";

const fmtErr = (err) => err?.response?.data?.detail || err?.message || "Something went wrong.";

const AdminDashboard = ({ adminUser, handleLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [donations, setDonations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [events, setEvents] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  const fetchDashboardData = async () => {
    try {
      const [s, d, c, v, m, p, e, sub] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/donations`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/volunteers`),
        axios.get(`${API}/admin/mentors`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/events`),
        axios.get(`${API}/admin/subscribers`),
      ]);
      setStats(s.data);
      setDonations(d.data);
      setContacts(c.data);
      setVolunteers(v.data);
      setMentors(m.data);
      setPartners(p.data);
      setEvents(e.data);
      setSubscribers(sub.data);
    } catch (err) {
      toast.error("Error loading dashboard: " + fmtErr(err));
    }
  };

  useEffect(() => {
    if (adminUser) fetchDashboardData();
  }, [adminUser]);

  const onLoginSubmit = async (ev) => {
    ev.preventDefault();
    setLoginLoading(true);
    try {
      await handleLogin(email, password);
      toast.success("Welcome back, Cherelle!");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoginLoading(false);
    }
  };

  const unsubscribe = async (subEmail) => {
    try {
      await axios.delete(`${API}/admin/subscribers/${subEmail}`);
      toast.success("Subscriber unsubscribed.");
      fetchDashboardData();
    } catch (err) {
      toast.error("Failed: " + fmtErr(err));
    }
  };

  if (!adminUser) {
    return (
      <div className="py-24 max-w-md mx-auto px-6 text-left animate-fade-in" data-testid="admin-login-container">
        <div className="bg-white border border-[#E5E5E5]/50 shadow-xl rounded-3xl p-8 space-y-6">
          <div className="text-center space-y-3">
            <img src={HEART_LOGO} alt="AGG" className="h-16 w-16 object-contain mx-auto" />
            <h2 className="font-serif text-2xl font-bold text-[#4B1E5A]">Administrative Login</h2>
            <p className="font-sans text-xs text-[#4A4A4A]">Manage every inquiry, application, and submission from one secure dashboard.</p>
          </div>
          <form onSubmit={onLoginSubmit} className="space-y-4" data-testid="admin-login-form">
            <div className="flex flex-col">
              <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">Administrative Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@agg.org" data-testid="admin-login-email-input" className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm" required />
            </div>
            <div className="flex flex-col">
              <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">Access Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-login-password-input" className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm" required />
            </div>
            <button type="submit" disabled={loginLoading} data-testid="admin-login-submit" className="w-full bg-[#4B1E5A] hover:bg-[#E94F9A] text-white rounded-xl py-3 text-sm font-semibold transition-all shadow-md">
              {loginLoading ? "Verifying..." : "Access Dashboard"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: "overview", name: "Overview" },
    { id: "content", name: "Website Content" },
    { id: "contacts", name: `General Contact (${contacts.filter((i) => !i.archived).length})` },
    { id: "partners", name: `Partnerships (${partners.filter((i) => !i.archived).length})` },
    { id: "volunteers", name: `Volunteers (${volunteers.filter((i) => !i.archived).length})` },
    { id: "mentors", name: `Mentors (${mentors.filter((i) => !i.archived).length})` },
    { id: "events", name: `Event RSVPs (${events.filter((i) => !i.archived).length})` },
    { id: "donations", name: `Donations (${donations.length})` },
    { id: "subscribers", name: `Newsletter (${subscribers.filter((i) => !i.archived).length})` },
    { id: "email", name: "Email & Marketing" },
  ];

  return (
    <div className="py-12 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-left animate-fade-in" data-testid="admin-dashboard-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-[#FFF5F9] pb-6 mb-8 gap-4">
        <div>
          <span className="font-sans text-xs text-[#C8A96A] uppercase tracking-widest font-semibold">AGG Operations Suite</span>
          <h1 className="font-serif text-3xl font-bold text-[#4B1E5A]">Administrative Control</h1>
        </div>
        <span className="font-sans text-xs bg-[#FFF5F9] text-[#E94F9A] px-3.5 py-1.5 rounded-full font-semibold">Logged in as Founder & President</span>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#4B1E5A] text-white p-5 rounded-2xl shadow-sm space-y-1">
            <p className="font-sans text-[11px] text-[#C8A96A] uppercase tracking-wider font-semibold">Total Donations</p>
            <p className="font-serif text-2xl font-bold" data-testid="stat-total-donations">${stats.donations?.total_amount}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5]/50 p-5 rounded-2xl shadow-sm space-y-1">
            <p className="font-sans text-[11px] text-[#4A4A4A] uppercase tracking-wider font-semibold">Volunteers</p>
            <p className="font-serif text-2xl font-bold text-[#4B1E5A]" data-testid="stat-volunteer-count">{stats.volunteers}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5]/50 p-5 rounded-2xl shadow-sm space-y-1">
            <p className="font-sans text-[11px] text-[#4A4A4A] uppercase tracking-wider font-semibold">Mentors</p>
            <p className="font-serif text-2xl font-bold text-[#4B1E5A]" data-testid="stat-mentor-count">{stats.mentors}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5]/50 p-5 rounded-2xl shadow-sm space-y-1">
            <p className="font-sans text-[11px] text-[#4A4A4A] uppercase tracking-wider font-semibold">Partnerships</p>
            <p className="font-serif text-2xl font-bold text-[#4B1E5A]" data-testid="stat-partner-count">{stats.partners}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5]/50 p-5 rounded-2xl shadow-sm space-y-1 col-span-2 md:col-span-1">
            <p className="font-sans text-[11px] text-[#4A4A4A] uppercase tracking-wider font-semibold">Newsletter Subs</p>
            <p className="font-serif text-2xl font-bold text-[#4B1E5A]" data-testid="stat-subscriber-count">{stats.subscribers}</p>
          </div>
        </div>
      )}

      <div className="flex overflow-x-auto gap-2 border-b border-[#FFF5F9] pb-3 mb-6 scrollbar-none font-sans text-xs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`admin-tab-btn-${t.id}`}
            className={`px-4 py-2.5 rounded-full font-semibold transition-all whitespace-nowrap ${activeTab === t.id ? "bg-[#E94F9A] text-white shadow-sm" : "bg-white border border-[#E5E5E5]/50 text-[#4A4A4A] hover:bg-[#FFF5F9]"}`}
          >
            {t.name}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="bg-white border border-[#E5E5E5]/50 rounded-2xl p-6 md:p-8 space-y-8" data-testid="panel-overview">
          <div className="space-y-2">
            <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">Operations Overview</h3>
            <p className="font-sans text-sm text-[#4A4A4A]">Every form submission is saved here and organized by category. Assign a status, mark as read, archive, add internal notes, and export to CSV.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-[#E5E5E5] rounded-xl p-5 space-y-3 text-center bg-[#FFF5F9]/10">
              <TrendingUp className="h-9 w-9 text-[#E94F9A] mx-auto" />
              <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">Track Every Lead</h4>
              <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed">Move inquiries through New → In Progress → Contacted → Approved → Closed.</p>
            </div>
            <div className="border border-[#E5E5E5] rounded-xl p-5 space-y-3 text-center bg-[#FFF5F9]/10">
              <DollarSign className="h-9 w-9 text-[#C8A96A] mx-auto" />
              <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">Donations</h4>
              <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed">Donations are processed securely via AutoBooks. Future giving records will appear here.</p>
            </div>
            <div className="border border-[#E5E5E5] rounded-xl p-5 space-y-3 text-center bg-[#FFF5F9]/10">
              <Award className="h-9 w-9 text-[#E94F9A] mx-auto" />
              <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">Stay Organized</h4>
              <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed">Search, filter, sort, and keep private team notes on every submission.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "contacts" && (
        <InquiryManager
          title="General Contact Inquiries" testId="contacts" collection="contacts" items={contacts} onChanged={fetchDashboardData}
          primary={(i) => i.subject || "(No subject)"}
          subtitle={(i) => `${i.name} • ${i.email}`}
          searchKeys={["name", "email", "subject", "message"]}
          details={[{ label: "Message", value: (i) => i.message }]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "Name", value: (i) => i.name },
            { label: "Email", value: (i) => i.email }, { label: "Subject", value: (i) => i.subject },
            { label: "Message", value: (i) => i.message }, { label: "Status", value: (i) => i.status },
            { label: "Notes", value: (i) => i.notes },
          ]}
        />
      )}

      {activeTab === "partners" && (
        <InquiryManager
          title="Partnership & Sponsorship Requests" testId="partners" collection="partners" items={partners} onChanged={fetchDashboardData}
          primary={(i) => i.organization}
          subtitle={(i) => `${i.contact_name} • ${i.email} • ${i.phone}`}
          badges={(i) => <span className="bg-[#4B1E5A] text-white text-[9px] px-2 py-0.5 rounded font-sans uppercase font-bold tracking-wider">{i.partner_type}</span>}
          searchKeys={["organization", "contact_name", "email", "phone", "partner_type", "message"]}
          details={[{ label: "Message", value: (i) => i.message }, { label: "Type", value: (i) => i.partner_type }]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "Organization", value: (i) => i.organization },
            { label: "Contact", value: (i) => i.contact_name }, { label: "Email", value: (i) => i.email },
            { label: "Phone", value: (i) => i.phone }, { label: "Type", value: (i) => i.partner_type },
            { label: "Message", value: (i) => i.message }, { label: "Status", value: (i) => i.status }, { label: "Notes", value: (i) => i.notes },
          ]}
        />
      )}

      {activeTab === "volunteers" && (
        <InquiryManager
          title="Volunteer Applications" testId="volunteers" collection="volunteers" items={volunteers} onChanged={fetchDashboardData}
          primary={(i) => `${i.first_name} ${i.last_name}`}
          subtitle={(i) => `${i.email} • ${i.phone}`}
          searchKeys={["first_name", "last_name", "email", "phone", "message"]}
          details={[
            { label: "Interests", value: (i) => (i.interest_areas || []).join(", ") },
            { label: "Message", value: (i) => i.message },
          ]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "First Name", value: (i) => i.first_name },
            { label: "Last Name", value: (i) => i.last_name }, { label: "Email", value: (i) => i.email },
            { label: "Phone", value: (i) => i.phone }, { label: "Interests", value: (i) => (i.interest_areas || []).join("; ") },
            { label: "Message", value: (i) => i.message }, { label: "Status", value: (i) => i.status }, { label: "Notes", value: (i) => i.notes },
          ]}
        />
      )}

      {activeTab === "mentors" && (
        <InquiryManager
          title="Mentor Applications" testId="mentors" collection="mentors" items={mentors} onChanged={fetchDashboardData}
          primary={(i) => `${i.first_name} ${i.last_name}`}
          subtitle={(i) => `${i.email} • ${i.phone}`}
          searchKeys={["first_name", "last_name", "email", "phone", "background", "why_mentor"]}
          details={[
            { label: "Background", value: (i) => i.background },
            { label: "Why Mentor", value: (i) => i.why_mentor },
          ]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "First Name", value: (i) => i.first_name },
            { label: "Last Name", value: (i) => i.last_name }, { label: "Email", value: (i) => i.email },
            { label: "Phone", value: (i) => i.phone }, { label: "Background", value: (i) => i.background },
            { label: "Why Mentor", value: (i) => i.why_mentor }, { label: "Status", value: (i) => i.status }, { label: "Notes", value: (i) => i.notes },
          ]}
        />
      )}

      {activeTab === "events" && (
        <InquiryManager
          title="Event Registrations (RSVPs)" testId="events" collection="events" items={events} onChanged={fetchDashboardData}
          primary={(i) => i.name}
          subtitle={(i) => `${i.email} • ${i.phone}`}
          badges={(i) => <span className="bg-[#FFF5F9] text-[#E94F9A] text-[9px] px-2 py-0.5 rounded font-sans font-bold">{i.event_title}</span>}
          searchKeys={["name", "email", "phone", "event_title"]}
          details={[
            { label: "Event", value: (i) => i.event_title },
            { label: "Seats", value: (i) => String(i.quantity) },
          ]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "Event", value: (i) => i.event_title },
            { label: "Name", value: (i) => i.name }, { label: "Email", value: (i) => i.email },
            { label: "Phone", value: (i) => i.phone }, { label: "Seats", value: (i) => i.quantity },
            { label: "Status", value: (i) => i.status }, { label: "Notes", value: (i) => i.notes },
          ]}
        />
      )}

      {activeTab === "donations" && (
        <InquiryManager
          title="Donations" testId="donations" collection="donations" items={donations} onChanged={fetchDashboardData}
          primary={(i) => i.donor_name || "Anonymous"}
          subtitle={(i) => `${i.donor_email || "No email"} • $${i.amount?.toFixed ? i.amount.toFixed(2) : i.amount}`}
          badges={(i) => <span className={`text-[9px] px-2 py-0.5 rounded font-sans font-bold uppercase ${i.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{i.payment_status}</span>}
          searchKeys={["donor_name", "donor_email", "package_id"]}
          details={[
            { label: "Amount", value: (i) => `$${i.amount}` },
            { label: "Payment Status", value: (i) => i.payment_status },
          ]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "Donor", value: (i) => i.donor_name },
            { label: "Email", value: (i) => i.donor_email }, { label: "Amount", value: (i) => i.amount },
            { label: "Payment Status", value: (i) => i.payment_status }, { label: "Workflow Status", value: (i) => i.status }, { label: "Notes", value: (i) => i.notes },
          ]}
        />
      )}

      {activeTab === "subscribers" && (
        <InquiryManager
          title="Newsletter Subscribers" testId="subscribers" collection="subscribers" items={subscribers} onChanged={fetchDashboardData}
          primary={(i) => i.email}
          subtitle={(i) => (i.active ? "Active subscriber" : "Unsubscribed")}
          badges={(i) => <span className={`text-[9px] px-2 py-0.5 rounded font-sans font-bold uppercase ${i.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{i.active ? "Active" : "Inactive"}</span>}
          searchKeys={["email"]}
          details={[]}
          csvColumns={[
            { label: "Date", value: (i) => i.timestamp }, { label: "Email", value: (i) => i.email },
            { label: "Active", value: (i) => i.active }, { label: "Status", value: (i) => i.status }, { label: "Notes", value: (i) => i.notes },
          ]}
          extraActions={(i) =>
            i.active ? (
              <button onClick={() => unsubscribe(i.email)} data-testid={`btn-unsubscribe-${i.email.replace(/[^a-z0-9]+/g, "-")}`} className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-full px-3 py-1.5 text-xs font-semibold">
                Unsubscribe
              </button>
            ) : null
          }
        />
      )}

      {activeTab === "email" && <EmailSettings />}
      {activeTab === "content" && <WebsiteContent />}
    </div>
  );
};

export default AdminDashboard;
